import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Trash2, Edit2, Plus, AlertTriangle, CheckCircle, Clock } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const PATRON_VACIO = {
  nombre: "",
  codigoInterno: "",
  serial: "",
  marca: "",
  modelo: "",
  numeroCertificado: "",
  laboratorioCertificador: "",
  fechaCalibracion: "",
  fechaVencimiento: "",
  normaTrazabilidad: "",
  cadenaTrazabilidad: "",
  alcanceCalibracion: "",
  incertidumbreDeclarada: "",
  observaciones: "",
};

const EstadoBadge = ({ estado }) => {
  if (estado === "vigente")
    return (
      <span className="flex items-center gap-1 text-green-700 bg-green-50 border border-green-200 text-xs px-2 py-0.5 rounded-full">
        <CheckCircle size={11} /> Vigente
      </span>
    );
  if (estado === "vencido")
    return (
      <span className="flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 text-xs px-2 py-0.5 rounded-full">
        <AlertTriangle size={11} /> Vencido
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-gray-500 bg-gray-100 text-xs px-2 py-0.5 rounded-full">
      <Clock size={11} /> Pendiente
    </span>
  );
};

const CalibPatronesPage = () => {
  const [patrones, setPatrones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ ...PATRON_VACIO });
  const [guardando, setGuardando] = useState(false);
  const [filtro, setFiltro] = useState("");

  const token = localStorage.getItem("token");

  const fetchPatrones = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calib-patrones`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatrones(res.data);
    } catch {
      toast.error("Error al cargar los patrones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatrones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ ...PATRON_VACIO });
    setShowModal(true);
  };

  const abrirEdicion = (patron) => {
    setEditando(patron);
    setForm({
      nombre: patron.nombre || "",
      codigoInterno: patron.codigoInterno || "",
      serial: patron.serial || "",
      marca: patron.marca || "",
      modelo: patron.modelo || "",
      numeroCertificado: patron.numeroCertificado || "",
      laboratorioCertificador: patron.laboratorioCertificador || "",
      fechaCalibracion: patron.fechaCalibracion?.slice(0, 10) || "",
      fechaVencimiento: patron.fechaVencimiento?.slice(0, 10) || "",
      normaTrazabilidad: patron.normaTrazabilidad || "",
      cadenaTrazabilidad: patron.cadenaTrazabilidad || "",
      alcanceCalibracion: patron.alcanceCalibracion || "",
      incertidumbreDeclarada: patron.incertidumbreDeclarada || "",
      observaciones: patron.observaciones || "",
    });
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!form.nombre.trim()) return toast.warning("El nombre es requerido");
    setGuardando(true);
    try {
      if (editando) {
        await axios.put(`${API}/calib-patrones/${editando._id}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Patrón actualizado");
      } else {
        await axios.post(`${API}/calib-patrones`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Patrón creado");
      }
      setShowModal(false);
      fetchPatrones();
    } catch (err) {
      const errores = err.response?.data?.errores;
      toast.error(
        errores?.length > 1
          ? `${errores[0]} (y ${errores.length - 1} más)`
          : err.response?.data?.message || "Error al guardar",
      );
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Eliminar este patrón?")) return;
    try {
      await axios.delete(`${API}/calib-patrones/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Patrón eliminado");
      fetchPatrones();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al eliminar");
    }
  };

  const patronesFiltrados = patrones.filter((p) => {
    const q = filtro.toLowerCase();
    return (
      p.nombre?.toLowerCase().includes(q) ||
      p.serial?.toLowerCase().includes(q) ||
      p.marca?.toLowerCase().includes(q)
    );
  });

  const set = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold">Patrones de Calibración</h2>
          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar patrón..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm w-full sm:w-60 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <Button onClick={abrirNuevo}>
              <Plus size={14} className="mr-1" /> Nuevo
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-400 text-sm italic">Cargando patrones...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow-sm text-sm">
              <thead className="bg-gray-100 text-gray-700 text-xs uppercase">
                <tr>
                  <th className="py-2 px-3 text-left">Nombre</th>
                  <th className="py-2 px-3 text-left">Serial</th>
                  <th className="py-2 px-3 text-left">Marca/Modelo</th>
                  <th className="py-2 px-3 text-left">Cert. N°</th>
                  <th className="py-2 px-3 text-left">Variables de medición</th>
                  <th className="py-2 px-3 text-left">Vencimiento</th>
                  <th className="py-2 px-3 text-left">Estado</th>
                  <th className="py-2 px-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {patronesFiltrados.map((p) => (
                  <tr key={p._id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{p.nombre}</td>
                    <td className="py-2 px-3 text-gray-500">{p.serial || "—"}</td>
                    <td className="py-2 px-3 text-gray-500">
                      {[p.marca, p.modelo].filter(Boolean).join(" / ") || "—"}
                    </td>
                    <td className="py-2 px-3 text-gray-500">{p.numeroCertificado || "—"}</td>
                    <td className="py-2 px-3 text-xs">
                      {p.variablesMedicion?.length ? (
                        <span
                          className="text-gray-600"
                          title="Se configuran (puntos, error e incertidumbre) desde las plantillas de calibración"
                        >
                          {p.variablesMedicion
                            .map((v) => (v.unidad ? `${v.nombre} (${v.unidad})` : v.nombre))
                            .join(", ")}
                        </span>
                      ) : (
                        <span
                          className="text-gray-400"
                          title="Se configuran desde las plantillas de calibración"
                        >
                          Sin configurar
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-gray-500">
                      {p.fechaVencimiento
                        ? new Date(p.fechaVencimiento).toLocaleDateString("es-CO")
                        : "—"}
                    </td>
                    <td className="py-2 px-3">
                      <EstadoBadge estado={p.estado} />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => abrirEdicion(p)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                        title="Editar"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleEliminar(p._id)}
                        className="text-red-400 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {patronesFiltrados.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-gray-400 italic">
                      No hay patrones registrados. Crea el primero.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal Form */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-2">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800">
                  {editando ? "Editar patrón" : "Nuevo patrón de calibración"}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-700">✕</button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nombre *" value={form.nombre} onChange={(v) => set("nombre", v)} placeholder="ej: Manómetro patrón digital" col={2} />
                <Field label="Código interno" value={form.codigoInterno} onChange={(v) => set("codigoInterno", v)} placeholder="ej: PAT-001" />
                <Field label="Serial" value={form.serial} onChange={(v) => set("serial", v)} />
                <Field label="Marca" value={form.marca} onChange={(v) => set("marca", v)} />
                <Field label="Modelo" value={form.modelo} onChange={(v) => set("modelo", v)} />
                <Field label="N° Certificado" value={form.numeroCertificado} onChange={(v) => set("numeroCertificado", v)} />
                <Field label="Laboratorio certificador" value={form.laboratorioCertificador} onChange={(v) => set("laboratorioCertificador", v)} col={2} />
                <div>
                  <label className="text-xs text-gray-500 font-medium">Fecha calibración</label>
                  <input
                    type="date"
                    className="border rounded w-full px-2 py-1.5 text-sm mt-0.5"
                    value={form.fechaCalibracion}
                    onChange={(e) => set("fechaCalibracion", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Fecha vencimiento</label>
                  <input
                    type="date"
                    className="border rounded w-full px-2 py-1.5 text-sm mt-0.5"
                    value={form.fechaVencimiento}
                    onChange={(e) => set("fechaVencimiento", e.target.value)}
                  />
                </div>
                <Field label="Norma de trazabilidad" value={form.normaTrazabilidad} onChange={(v) => set("normaTrazabilidad", v)} placeholder="ej: NTC ISO/IEC 17025" col={2} />
                <Field label="Cadena de trazabilidad" value={form.cadenaTrazabilidad} onChange={(v) => set("cadenaTrazabilidad", v)} placeholder="ej: INM → BIPM" col={2} />
                <Field label="Alcance de calibración" value={form.alcanceCalibracion} onChange={(v) => set("alcanceCalibracion", v)} placeholder="ej: 0 a 300 mmHg" />
                <Field label="Incertidumbre declarada (texto)" value={form.incertidumbreDeclarada} onChange={(v) => set("incertidumbreDeclarada", v)} placeholder="ej: ± 0.5 mmHg (k=2)" />
                <Field label="Observaciones" value={form.observaciones} onChange={(v) => set("observaciones", v)} col={2} />
              </div>
              <p className="text-xs text-gray-500 mt-4 bg-gray-50 border rounded p-2">
                Las variables de medición del patrón (puntos del certificado, error e
                incertidumbre) se configuran en cada plantilla de calibración y aplican a
                todas las plantillas que usen este patrón.
              </p>

              <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button onClick={handleGuardar} disabled={guardando}>
                  {guardando ? "Guardando..." : editando ? "Actualizar" : "Crear patrón"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Helper inline field component
const Field = ({ label, value, onChange, placeholder = "", col = 1 }) => (
  <div className={col === 2 ? "sm:col-span-2" : ""}>
    <label className="text-xs text-gray-500 font-medium">{label}</label>
    <input
      type="text"
      className="border rounded w-full px-2 py-1.5 text-sm mt-0.5"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

export default CalibPatronesPage;
